import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
} from "@mui/material";
import { MuiTelInput } from "mui-tel-input";
import {
  PhoneAuthProvider,
  RecaptchaVerifier,
  linkWithCredential,
  onAuthStateChanged,
  type User,
} from "firebase/auth";

import { getApiUrl } from "@/constants";
import httpClient from "@/dataprovider/http-client";
import { authObj, recaptchaConfigReady } from "@/firebase-config";
import { getFirebasePhoneAuthMessage } from "@/utils/firebase-auth-messages";

const RECAPTCHA_CONTAINER_ID = "link-phone-recaptcha-container";

function normalizePhoneNumber(value: string): string {
  return value.trim().replace(/[\s\-()]/g, "");
}

function isE164(value: string): boolean {
  return /^\+[1-9]\d{7,14}$/.test(value);
}

function getErrorMessage(error: unknown): string | null {
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }
  return null;
}

type LinkPhoneDialogProps = {
  variant?: "button" | "banner";
};

export default function LinkPhoneDialog({
  variant = "button",
}: LinkPhoneDialogProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState<User | null>(authObj.currentUser);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [linking, setLinking] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);

  useEffect(() => onAuthStateChanged(authObj, setUser), []);

  useEffect(
    () => () => {
      recaptchaRef.current?.clear();
      recaptchaRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (!open) {
      setPhoneNumber("");
      setOtp("");
      setVerificationId(null);
      setOtpCooldown(0);
      setErrorMessage(null);
      setInfoMessage(null);
    }
  }, [open]);

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setOtpCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpCooldown]);

  const canSendOtp = useMemo(
    () => isE164(normalizePhoneNumber(phoneNumber)) && !sendingOtp,
    [phoneNumber, sendingOtp]
  );
  const isPhoneLinked = Boolean(user?.phoneNumber);

  const handleOpen = (): void => setOpen(true);
  const handleClose = (): void => setOpen(false);

  const updatePhoneInDb = async (linkedPhoneNumber: string): Promise<void> => {
    await httpClient(`${getApiUrl()}/admins/me/phone-number`, {
      method: "PUT",
      body: JSON.stringify({ phoneNumber: linkedPhoneNumber }),
    });
  };

  const handleSendOtp = async (): Promise<void> => {
    setErrorMessage(null);
    setInfoMessage(null);
    if (!user) {
      setErrorMessage("You must be logged in to link a phone number");
      return;
    }

    const normalized = normalizePhoneNumber(phoneNumber);
    if (!isE164(normalized)) {
      setErrorMessage("Enter phone in E.164 format (e.g. +919876543210)");
      return;
    }
    if (otpCooldown > 0) {
      setInfoMessage(`Please wait ${otpCooldown}s before requesting a new OTP`);
      return;
    }

    setSendingOtp(true);
    try {
      await recaptchaConfigReady;
      recaptchaRef.current?.clear();
      recaptchaRef.current = new RecaptchaVerifier(authObj, RECAPTCHA_CONTAINER_ID, {
        size: "invisible",
      });
      const verifier = recaptchaRef.current;
      await verifier.render();
      const provider = new PhoneAuthProvider(authObj);
      const nextVerificationId = await provider.verifyPhoneNumber(normalized, verifier);
      setVerificationId(nextVerificationId);
      setOtpCooldown(30);
      setInfoMessage("OTP sent. Please enter the code to link this phone.");
    } catch (error: unknown) {
      setErrorMessage(getFirebasePhoneAuthMessage(error));
    } finally {
      setSendingOtp(false);
    }
  };

  const handleLinkPhone = async (): Promise<void> => {
    setErrorMessage(null);
    if (!user) {
      setErrorMessage("You must be logged in to link a phone number");
      return;
    }
    if (!verificationId) {
      setErrorMessage("Please request OTP first");
      return;
    }
    const normalizedOtp = otp.trim();
    if (!normalizedOtp) {
      setErrorMessage("Enter the OTP code");
      return;
    }

    setLinking(true);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, normalizedOtp);
      const linkedCredential = await linkWithCredential(user, credential);
      const linkedPhoneNumber =
        linkedCredential.user.phoneNumber ?? normalizePhoneNumber(phoneNumber);
      await updatePhoneInDb(linkedPhoneNumber);
      setUser(linkedCredential.user);
      setInfoMessage("Phone number linked successfully.");
      setOpen(false);
    } catch (error: unknown) {
      setErrorMessage(
        getErrorMessage(error) ??
          getFirebasePhoneAuthMessage(error) ??
          "Could not complete phone linking. Please try again."
      );
    } finally {
      setLinking(false);
    }
  };

  return (
    <>
      {variant === "banner" && !isPhoneLinked ? (
        <Alert
          severity="info"
          sx={{ my: 1, alignItems: "center" }}
          action={
            <Button color="inherit" onClick={handleOpen} size="small" variant="outlined">
              Link phone
            </Button>
          }
        >
          Link your phone number for OTP login. Email/password login will continue to work.
        </Alert>
      ) : variant === "button" ? (
        <Button color="inherit" onClick={handleOpen} size="small">
          Link Phone
        </Button>
      ) : null}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>Link phone authentication</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
            {!errorMessage && infoMessage ? <Alert severity="info">{infoMessage}</Alert> : null}
            <Typography variant="body2" color="text.secondary">
              Add a phone number to this account for OTP sign-in later. Existing email/password
              login stays unchanged.
            </Typography>
            <MuiTelInput
              label="Phone number"
              value={phoneNumber}
              onChange={(value) => setPhoneNumber(value)}
              fullWidth
              defaultCountry="IN"
              forceCallingCode
              langOfCountryName="en"
            />
            <Button
              variant="outlined"
              onClick={handleSendOtp}
              disabled={!canSendOtp || otpCooldown > 0}
            >
              {sendingOtp
                ? "Sending OTP..."
                : otpCooldown > 0
                  ? `Resend in ${otpCooldown}s`
                  : "Send OTP"}
            </Button>
            {verificationId ? (
              <TextField
                label="OTP code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                fullWidth
              />
            ) : null}
            <div id={RECAPTCHA_CONTAINER_ID} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleLinkPhone}
            variant="contained"
            disabled={!verificationId || linking}
          >
            {linking ? "Linking..." : "Verify & Link"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
